import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';

import tr from '@/messages/tr.json';
import en from '@/messages/en.json';
import bs from '@/messages/bs.json';
import de from '@/messages/de.json';
import { detectBrowserLang } from '@/types/lang';

/**
 * i18next initialization (CLAUDE.md §3, §9).
 *
 * - ICU plugin: required for Bosnian Slavic plurals (one/few/many/other) and
 *   ordinal formatting (CLAUDE.md §10 i18n rules).
 * - Initial language: detected from `navigator.language`, falls back to 'tr'
 *   (most parents are Turkish-speaking).
 * - User can override via Settings; persisted to localStorage in Sprint 5.
 * - All 4 language resources are bundled (separate i18n vendor chunk per
 *   vite.config.ts) so language switching is instant with no reload.
 */

const STORAGE_KEY = 'family.uiLang';
const initialLng =
  typeof localStorage !== 'undefined'
    ? localStorage.getItem(STORAGE_KEY) || detectBrowserLang()
    : detectBrowserLang();

void i18n
  .use(ICU)
  .use(initReactI18next)
  .init({
    resources: {
      tr: { translation: tr },
      en: { translation: en },
      bs: { translation: bs },
      de: { translation: de },
    },
    lng: initialLng,
    fallbackLng: 'tr',
    interpolation: {
      escapeValue: false, // React already escapes
    },
    returnNull: false,
  });

// Oturum 8 — Begüm's UX request: auto-persist any language change.
// Previously each component (Settings, Onboarding, future Login lang chip bar)
// had to manually setItem after calling changeLanguage. Centralised listener
// removes that duplication and guarantees consistency: pick a language anywhere
// in the app → localStorage tracks it for next visit.
i18n.on('languageChanged', (lng) => {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    // Safari private mode / storage quota — ignore (lang stays in-memory).
  }
});

// First-time visitor: write detected lang to localStorage on boot so the very
// next page visit (e.g. PWA install reopen) doesn't re-run detection.
if (typeof localStorage !== 'undefined') {
  try {
    if (!localStorage.getItem(STORAGE_KEY)) {
      localStorage.setItem(STORAGE_KEY, initialLng);
    }
  } catch {
    /* ignore */
  }
}

export default i18n;
